import {
  FeatureType,
  HexEdge,
  stringToFeature,
  stringToHexEdges,
  stringToTerrain,
  TerrainType
} from "@hexcrawl/shared-utils";

interface TilePropertyJSON {
  name: string;
  type: string;
  value: string;
}

interface TileJSON {
  id: number;
  properties?: TilePropertyJSON[];
}

interface TilesetJSON {
  name: string;
  image: string;
  imagewidth: number;
  imageheight: number;
  tilewidth: number;
  tileheight: number;
  tilecount: number;
  tiles: TileJSON[];
}

export class TileData {
  public id: number;
  public terrain: TerrainType;
  public feature: FeatureType;
  public rivers: HexEdge;
  public roads: HexEdge;
  public weight: number;

  constructor(id: number, terrain: TerrainType, feature: FeatureType, rivers: HexEdge, roads: HexEdge, weight: number = 1.0) {
    this.id = id;
    this.terrain = terrain;
    this.feature = feature;
    this.rivers = rivers;
    this.roads = roads;
    this.weight = weight;
  }

  static fromJSON(json: TileJSON, idOffset: number = 0): TileData {
    let terrain: TerrainType = TerrainType.NONE;
    let feature: FeatureType = FeatureType.NONE;
    let rivers: HexEdge = HexEdge.NONE;
    let roads: HexEdge = HexEdge.NONE;
    let weight: number = 1.0;

    if (json.properties) {
      for (const prop of json.properties) {
        switch (prop.name) {
          case 'terrain':
            terrain = stringToTerrain(prop.value);
            break;
          case 'feature':
            feature = stringToFeature(prop.value);
            break;
          case 'river':
            rivers = stringToHexEdges(prop.value);
            break;
          case 'road':
            roads = stringToHexEdges(prop.value);
            break;
          case 'weight':
            weight = parseFloat(prop.value);
            break;
        }
      }
    }

    return new TileData(
      json.id + idOffset,
      terrain,
      feature,
      rivers,
      roads,
      weight
    );
  }
}

export class TilesetData {
  public name: string;
  public image: string;
  public startingId: number;
  public tiles: TileData[];

  constructor(name: string, image: string, startingId: number, tiles: TileData[]) {
    this.name = name;
    this.image = image;
    this.startingId = startingId;
    this.tiles = tiles;
  }

  static fromJSON(json: TilesetJSON, startingId: number = 1): TilesetData {
    const tiles: TileData[] = [];
    for (const tileJson of json.tiles) {
      const tile = TileData.fromJSON(tileJson, startingId);
      tiles.push(tile);
      console.log(`Loaded tile ${tile.id}: (terrain: ${tile.terrain}, feature: ${tile.feature}, rivers: ${tile.rivers}, roads: ${tile.roads}, weight: ${tile.weight})`);
    }
    return new TilesetData(json.name, json.image, startingId, tiles);
  }
}

/**
 * Parse a Tiled JSON tileset file
 */
export async function loadTilesetJSON(url: string, startingId: number): Promise<TilesetData> {
  console.log(`Loading tileset from ${url}`);
  const response = await fetch(url);
  const json: TilesetJSON = await response.json();
  const tileSet = TilesetData.fromJSON(json, startingId);
  console.log(`Loaded tileset ${tileSet.name} with image ${tileSet.image}.`);
  return tileSet;
}